import { LitElement, html, css, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import "../components/index.js";
import { toast } from "../components/index.js";
import {
  listApiKeys,
  createApiKey,
  revokeApiKey,
  FEATURES,
  type ApiKey,
  type ApiKeyCreated,
  type Feature,
  type GitAccess,
} from "../api/apiKeys.js";
import { listTeams, type Team } from "../api/teams.js";
import { listWorkspaces, type Workspace } from "../api/workspaces.js";
import { listRepositories, type RepositoryInfo } from "../api/repositories.js";
import { listPasskeys } from "../api/passkeys.js";
import { webauthnSupported, passkeyFailureMessage } from "../api/webauthn.js";
import { requestStepUpOtp, confirmStepUpOtp, verifyWithPasskey } from "../api/stepup.js";
import { humanError, parseError } from "../api/errors.js";
import type { BnColumn, BnRow } from "../components/index.js";

/**
 * Lifetime is the shared expiry choice for both create flows: a preset, a
 * custom calendar date, or unlimited (which demands an extra confirmation).
 */
type Lifetime = "7d" | "30d" | "90d" | "1y" | "custom" | "unlimited";

/** LIFETIMES drives the expiration `<select>`, in display order. */
const LIFETIMES: ReadonlyArray<{ value: Lifetime; label: string }> = [
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
  { value: "1y", label: "1 year" },
  { value: "custom", label: "Custom date" },
  { value: "unlimited", label: "Unlimited" },
];

const DAY_SECONDS = 24 * 60 * 60;

/** Preset lifetimes in seconds (the `expiresIn` the create endpoint expects). */
const PRESET_SECONDS: Record<string, number> = {
  "7d": 7 * DAY_SECONDS,
  "30d": 30 * DAY_SECONDS,
  "90d": 90 * DAY_SECONDS,
  "1y": 365 * DAY_SECONDS,
};

/**
 * page-api-keys is the API view (route /api-keys, nav label "API"). It lists the
 * caller's credentials in two cards — full-authority API keys and scoped
 * fine-grained tokens — and mints new ones: both flows share a lifetime picker
 * (presets, custom date, or unlimited behind a confirm), a step-up "Verify
 * it's you" gate (passkey or email code — the server refuses creation without
 * a fresh verification grant), and the one-time plaintext reveal with a "you
 * won't see this again" warning; the fine-grained form adds team/workspace
 * scoping and a per-feature permission grid. Revokes
 * sit behind a confirm. A "use it" curl snippet shows the exchange -> render
 * flow, and the API reference (RapiDoc over the server's /v1/openapi.json)
 * renders below; rapidoc is imported lazily so its bundle loads only on this
 * page. Every color/space/radius/font comes from a --bino-* token; all API
 * access goes through the typed apiKeys/teams/workspaces clients.
 */
@customElement("page-api-keys")
export class PageApiKeys extends LitElement {
  @state() private keys: ApiKey[] = [];
  @state() private loading = true;
  @state() private loadError = "";

  // Create flow: modal open, form value, in-flight, and the one-time result.
  // `creating`/`created` are shared with the fine-grained flow — only one
  // create modal is ever open at a time.
  @state() private createOpen = false;
  @state() private newName = "";
  @state() private creating = false;
  @state() private created: ApiKeyCreated | null = null;

  // Lifetime picker, shared by both create flows; reset on each modal open.
  // `unlimitedConfirm` holds which submit path is waiting on the "never
  // expires" confirmation modal (null = not asking).
  @state() private lifetime: Lifetime = "90d";
  @state() private customDate = "";
  @state() private unlimitedConfirm: "general" | "scoped" | null = null;

  // Step-up verification: which submit path is paused behind the "Verify
  // it's you" modal (null = closed), the OTP id/code once "Email me a code"
  // ran, and one in-flight flag spanning the passkey ceremony and both OTP
  // round trips. hasPasskeys resolves lazily on first open (null = unknown)
  // so the passkey button only shows when it can actually work.
  @state() private verifyFor: "general" | "scoped" | null = null;
  @state() private verifyOtpId = "";
  @state() private verifyCode = "";
  @state() private verifyBusy = false;
  @state() private hasPasskeys: boolean | null = null;

  /** Whether "Email me a code" already ran this round — derived, not stored:
   * verifyOtpId is set exactly when a code went out and cleared on open. */
  private get verifyOtpSent(): boolean {
    return this.verifyOtpId !== "";
  }

  // Fine-grained create flow: modal open + the scope form values.
  @state() private scopedOpen = false;
  @state() private scopedName = "";
  @state() private scopedTeamId = ""; // "" = all teams
  @state() private scopedWorkspaceId = ""; // "" = entire team
  @state() private scopedRepositoryId = ""; // "" = no repository; excludes a workspace
  @state() private scopedGitAccess: GitAccess = "read";
  @state() private scopedFeatures: Feature[] = [];

  // Teams/workspaces for the scope selects and the Scope column, lazy-loaded
  // the first time they're needed (see ensureScopeData).
  @state() private teams: Team[] = [];
  @state() private workspaces: Workspace[] = [];
  private scopeDataLoaded = false;
  // The picked team's repositories. Empty when it has none, and when the
  // server has no repositories: the select is then hidden.
  @state() private teamRepositories: RepositoryInfo[] = [];
  // Repository names for the Scope column, by id.
  @state() private repositoryNames: Record<string, string> = {};

  // Revoke flow: the key pending confirmation and the in-flight flag.
  @state() private revokeTarget: ApiKey | null = null;
  @state() private revoking = false;

  static override styles = css`
    :host {
      display: block;
    }
    .head {
      display: flex;
      align-items: flex-start;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-lg);
    }
    .head-text {
      flex: 1;
      min-width: 0;
    }
    h1 {
      margin: 0 0 var(--bino-space-xs);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-lg);
      font-weight: 600;
      color: var(--bino-text);
    }
    .stack {
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-lg);
    }
    .load-error {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--bino-space-md);
      padding: var(--bino-space-xl) var(--bino-space-lg);
      text-align: center;
      font-family: var(--bino-font-sans);
      color: var(--bino-text-secondary);
    }

    /* Copyable code / token surfaces. */
    .copy-row {
      display: flex;
      align-items: stretch;
      gap: var(--bino-space-sm);
    }
    .code {
      flex: 1;
      min-width: 0;
      margin: 0;
      padding: var(--bino-space-md);
      background: var(--bino-surface-inset);
      border: 1px solid var(--bino-border);
      border-radius: var(--bino-radius);
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
      line-height: 1.6;
      color: var(--bino-text);
      white-space: pre;
      overflow-x: auto;
    }
    .token {
      display: flex;
      align-items: center;
      padding: var(--bino-space-sm) var(--bino-space-md);
      white-space: nowrap;
    }

    .warn {
      display: flex;
      gap: var(--bino-space-sm);
      align-items: flex-start;
      margin-top: var(--bino-space-md);
      padding: var(--bino-space-md);
      background: var(--bino-warning-bg);
      border: 1px solid var(--bino-warning-border);
      border-radius: var(--bino-radius);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-base);
      color: var(--bino-warning-text);
    }
    .warn-icon {
      flex-shrink: 0;
      font-weight: 700;
    }

    .reveal-label {
      margin: 0 0 var(--bino-space-sm);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-base);
      font-weight: 500;
      color: var(--bino-text-muted);
    }
    .snippet-lead {
      margin: 0 0 var(--bino-space-md);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
    .confirm-name {
      font-family: var(--bino-font-mono);
      color: var(--bino-text);
    }
    .modal-copy {
      margin: 0 0 var(--bino-space-md);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-base);
      color: var(--bino-text-secondary);
    }
    .verify-passkey {
      margin-bottom: var(--bino-space-md);
    }

    /* Native form controls in the create modals (same recipe as
       page-workspaces' team select). */
    select,
    input[type="date"] {
      width: 100%;
      box-sizing: border-box;
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text);
      padding: 0.5rem 0.6875rem;
      border: 1px solid var(--bino-border-light);
      border-radius: var(--bino-radius);
      background: var(--bino-surface-subtle);
    }
    select:focus,
    input[type="date"]:focus {
      outline: none;
      border-color: var(--bino-primary);
      box-shadow: 0 0 0 3px var(--bino-focus-ring);
    }
    .lifetime-row {
      display: flex;
      gap: var(--bino-space-sm);
    }
    .lifetime-row > * {
      flex: 1;
      min-width: 0;
    }

    /* Fine-grained permission grid: "Select all" above a hairline, then the
       feature checkboxes two-up. */
    .feature-head {
      margin-bottom: var(--bino-space-sm);
      padding-bottom: var(--bino-space-sm);
      border-bottom: 1px solid var(--bino-border);
    }
    .feature-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: var(--bino-space-sm) var(--bino-space-md);
    }

    rapi-doc {
      display: block;
      width: 100%;
      height: 78vh;
    }
    .spec-link {
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      text-decoration: none;
    }
    .spec-link:hover {
      color: var(--bino-text);
      text-decoration: underline;
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    // Side-effect import registers <rapi-doc>; dynamic so the ~700 KB bundle
    // is a lazy chunk that loads only when this page is visited.
    void import("rapidoc");
    void this.load();
  }

  private async load() {
    this.loading = true;
    this.loadError = "";
    try {
      this.keys = await listApiKeys();
      // The Scope column needs team/workspace names; fetch them only when
      // there is actually a scoped token to label.
      if (this.keys.some((k) => k.kind === "scoped")) void this.ensureScopeData();
    } catch (e) {
      this.loadError = humanError(e);
    } finally {
      this.loading = false;
    }
  }

  /**
   * ensureScopeData lazily loads teams + workspaces the first time scope data
   * is needed (opening the fine-grained modal, or labeling the Scope column).
   * Failures reset the guard so the next trigger retries.
   */
  private async ensureScopeData() {
    if (this.scopeDataLoaded) return;
    this.scopeDataLoaded = true;
    try {
      const [teams, workspaces] = await Promise.all([listTeams(), listWorkspaces()]);
      this.teams = teams;
      this.workspaces = workspaces;
    } catch (e) {
      this.scopeDataLoaded = false;
      toast(humanError(e), "error");
      return;
    }
    const repoTeams = new Set(
      this.keys.filter((k) => k.scope?.repository && k.scope.team).map((k) => k.scope!.team!),
    );
    await Promise.all([...repoTeams].map((t) => this.loadRepositories(t)));
  }

  /**
   * loadRepositories fetches a team's repositories and remembers their names.
   * A failure yields none: the routes exist only while the server has
   * repositories on, and the form works without them.
   */
  private async loadRepositories(teamId: string): Promise<RepositoryInfo[]> {
    try {
      const repos = await listRepositories(teamId);
      const names = { ...this.repositoryNames };
      for (const r of repos) names[r.id] = r.name;
      this.repositoryNames = names;
      return repos;
    } catch {
      return [];
    }
  }

  private async pickTeam(teamId: string) {
    this.scopedTeamId = teamId;
    this.scopedWorkspaceId = "";
    this.pickRepository("");
    this.teamRepositories = [];
    if (!teamId) return;
    const repos = await this.loadRepositories(teamId);
    // A slower answer for a team no longer picked must not fill the select.
    if (this.scopedTeamId === teamId) this.teamRepositories = repos;
  }

  /** A repository scope excludes a workspace one; git needs a repository. */
  private pickRepository(id: string) {
    this.scopedRepositoryId = id;
    if (id) this.scopedWorkspaceId = "";
    else this.scopedFeatures = this.scopedFeatures.filter((f) => f !== "git");
  }

  /** git is grantable only on a repository-scoped key. */
  private featureEnabled(id: Feature): boolean {
    return id !== "git" || this.scopedRepositoryId !== "";
  }

  private teamName(id: string): string {
    const t = this.teams.find((x) => x.id === id);
    return t ? t.displayName || t.name : id;
  }

  private workspaceName(id: string): string {
    return this.workspaces.find((w) => w.id === id)?.name ?? id;
  }

  private async copy(text: string, label = "Copied to clipboard") {
    try {
      await navigator.clipboard.writeText(text);
      toast(label, "success");
    } catch {
      toast("Couldn't copy. Select the text and copy it yourself.", "error");
    }
  }

  // ---- Lifetime (shared by both create flows) --------------------------------

  private resetLifetime() {
    this.lifetime = "90d";
    this.customDate = "";
  }

  /**
   * expiresInSeconds maps the picked lifetime to the create endpoint's
   * `expiresIn`: preset seconds, 0 for unlimited, or seconds until the end of
   * the custom date's day. null (missing/invalid/past/beyond 10 years) means
   * "not submittable" — the create buttons disable on it.
   */
  private expiresInSeconds(): number | null {
    if (this.lifetime === "unlimited") return 0;
    if (this.lifetime !== "custom") return PRESET_SECONDS[this.lifetime] ?? null;
    if (!this.customDate) return null;
    // A bare date parses as UTC midnight; the time suffix makes it local
    // end-of-day, so "expires today" still means later today.
    const end = new Date(this.customDate + "T23:59:59");
    if (Number.isNaN(end.getTime())) return null;
    const seconds = Math.floor((end.getTime() - Date.now()) / 1000);
    if (seconds <= 0 || seconds > 10 * 365 * DAY_SECONDS) return null;
    return seconds;
  }

  // ---- Create ----------------------------------------------------------------

  private openCreate() {
    this.newName = "";
    this.created = null;
    this.resetLifetime();
    this.createOpen = true;
  }

  private closeCreate() {
    if (this.creating || this.unlimitedConfirm !== null || this.verifyFor !== null) return;
    this.createOpen = false;
  }

  private async submitCreate(confirmed = false, verification?: string) {
    const name = this.newName.trim();
    const expiresIn = this.expiresInSeconds();
    if (!name || expiresIn === null || this.creating) return;
    if (this.lifetime === "unlimited" && !confirmed) {
      this.unlimitedConfirm = "general";
      return;
    }
    // No grant yet: pause here and open the "Verify it's you" modal, which
    // resumes this submit with one. Verification runs after the unlimited
    // confirm so the grant stays fresh relative to its 5-minute TTL.
    if (!verification) {
      this.openVerify("general");
      return;
    }
    this.creating = true;
    try {
      const result = await createApiKey({ name, expiresIn, verification });
      this.created = result;
      this.keys = [
        {
          id: result.id,
          name: result.name,
          prefix: result.prefix,
          created: result.created,
          lastUsed: "",
          expires: result.expires,
          kind: result.kind,
        },
        ...this.keys,
      ];
      toast("API key created", "success");
    } catch (e) {
      this.createFailed(e, "general");
    } finally {
      this.creating = false;
    }
  }

  // ---- Create (fine-grained) -------------------------------------------------

  private openScoped() {
    this.scopedName = "";
    this.scopedTeamId = "";
    this.scopedWorkspaceId = "";
    this.scopedRepositoryId = "";
    this.scopedGitAccess = "read";
    this.teamRepositories = [];
    this.scopedFeatures = [];
    this.created = null;
    this.resetLifetime();
    this.scopedOpen = true;
    void this.ensureScopeData();
  }

  private closeScoped() {
    if (this.creating || this.unlimitedConfirm !== null || this.verifyFor !== null) return;
    this.scopedOpen = false;
  }

  private toggleFeature(id: Feature, on: boolean) {
    const rest = this.scopedFeatures.filter((f) => f !== id);
    this.scopedFeatures = on ? [...rest, id] : rest;
  }

  private scopedValid(): boolean {
    return (
      this.scopedName.trim() !== "" &&
      this.scopedFeatures.length > 0 &&
      this.expiresInSeconds() !== null
    );
  }

  private async submitScoped(confirmed = false, verification?: string) {
    const expiresIn = this.expiresInSeconds();
    if (!this.scopedValid() || expiresIn === null || this.creating) return;
    if (this.lifetime === "unlimited" && !confirmed) {
      this.unlimitedConfirm = "scoped";
      return;
    }
    // Same pause as submitCreate: no grant means the verify modal takes over
    // and resumes this submit once one is earned.
    if (!verification) {
      this.openVerify("scoped");
      return;
    }
    this.creating = true;
    try {
      const result = await createApiKey({
        name: this.scopedName.trim(),
        expiresIn,
        kind: "scoped",
        scope: {
          team: this.scopedTeamId || undefined,
          workspace: this.scopedWorkspaceId || undefined,
          repository: this.scopedRepositoryId || undefined,
          gitAccess: this.scopedFeatures.includes("git") ? this.scopedGitAccess : undefined,
          features: [...this.scopedFeatures],
        },
        verification,
      });
      this.created = result;
      this.keys = [
        {
          id: result.id,
          name: result.name,
          prefix: result.prefix,
          created: result.created,
          lastUsed: "",
          expires: result.expires,
          kind: result.kind ?? "scoped",
          scope: result.scope,
        },
        ...this.keys,
      ];
      toast("Fine-grained token created", "success");
    } catch (e) {
      this.createFailed(e, "scoped");
    } finally {
      this.creating = false;
    }
  }

  /** confirmUnlimited resumes whichever submit path the confirmation paused. */
  private confirmUnlimited() {
    const kind = this.unlimitedConfirm;
    this.unlimitedConfirm = null;
    if (kind === "general") void this.submitCreate(true);
    else if (kind === "scoped") void this.submitScoped(true);
  }

  /**
   * createFailed routes a create error: verification codes reopen the verify
   * modal (the grant was missing, expired, or already burned — the form state
   * is untouched, so the user only needs to verify again), everything else
   * just toasts.
   */
  private createFailed(e: unknown, kind: "general" | "scoped") {
    toast(humanError(e), "error");
    const { code } = parseError(e);
    if (code === "verification_required" || code === "verification_invalid") {
      this.openVerify(kind);
    }
  }

  // ---- Step-up verification ----------------------------------------------------

  /**
   * openVerify pauses a submit path behind the "Verify it's you" modal. The
   * OTP sub-state resets on every open — grants and codes are single-use, so
   * anything left from a previous round could only produce otp_invalid.
   */
  private openVerify(kind: "general" | "scoped") {
    this.verifyOtpId = "";
    this.verifyCode = "";
    this.verifyFor = kind;
    void this.ensurePasskeyInfo();
  }

  private closeVerify() {
    if (this.verifyBusy) return;
    this.verifyFor = null;
  }

  /**
   * ensurePasskeyInfo lazily resolves whether the passkey button is worth
   * showing: WebAuthn support plus at least one registered credential. Runs
   * once per page life; a failed lookup stays null so the next open retries
   * (meanwhile the modal simply offers only the email path).
   */
  private async ensurePasskeyInfo() {
    if (this.hasPasskeys !== null) return;
    if (!webauthnSupported()) {
      this.hasPasskeys = false;
      return;
    }
    try {
      this.hasPasskeys = (await listPasskeys()).length > 0;
    } catch {
      // Left null on purpose — see the doc comment above.
    }
  }

  /** resumeVerified closes the verify modal and resumes the paused submit
   * with the grant (confirmed=true — the unlimited gate, if due, already ran). */
  private resumeVerified(grant: string) {
    const kind = this.verifyFor;
    this.verifyFor = null;
    if (kind === "general") void this.submitCreate(true, grant);
    else if (kind === "scoped") void this.submitScoped(true, grant);
  }

  private async verifyPasskey() {
    if (this.verifyBusy) return;
    this.verifyBusy = true;
    try {
      const grant = await verifyWithPasskey("create_api_key");
      this.resumeVerified(grant);
    } catch (err) {
      // A dismissed authenticator prompt is a cancel: keep the modal open so
      // the user can retry or fall back to email, and say nothing (same
      // treatment as page-security's add flow). Real failures toast.
      const msg = passkeyFailureMessage(err);
      if (msg) toast(msg, "error");
    } finally {
      this.verifyBusy = false;
    }
  }

  private async sendVerifyCode() {
    if (this.verifyBusy) return;
    this.verifyBusy = true;
    try {
      const { otpId } = await requestStepUpOtp();
      this.verifyOtpId = otpId;
      this.verifyCode = "";
    } catch (e) {
      toast(humanError(e), "error");
    } finally {
      this.verifyBusy = false;
    }
  }

  private async confirmVerifyCode() {
    const code = this.verifyCode.trim();
    if (code === "" || this.verifyOtpId === "" || this.verifyBusy) return;
    this.verifyBusy = true;
    try {
      const grant = await confirmStepUpOtp(this.verifyOtpId, code, "create_api_key");
      this.resumeVerified(grant);
    } catch (e) {
      // otp_invalid covers wrong, expired, and foreign codes alike; the modal
      // stays open so the user can retype, or close and reopen for a fresh
      // code.
      toast(humanError(e), "error");
    } finally {
      this.verifyBusy = false;
    }
  }

  // ---- Revoke ----------------------------------------------------------------

  private async confirmRevoke() {
    const target = this.revokeTarget;
    if (!target || this.revoking) return;
    this.revoking = true;
    try {
      await revokeApiKey(target.id);
      this.keys = this.keys.filter((k) => k.id !== target.id);
      toast("API key revoked", "success");
      this.revokeTarget = null;
    } catch (e) {
      toast(humanError(e), "error");
    } finally {
      this.revoking = false;
    }
  }

  // ---- Formatting ------------------------------------------------------------

  private fmtDate(iso: string): string {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  }

  private snippet(): string {
    const origin = window.location.origin;
    return [
      "# 1. Exchange your API key for a short-lived token",
      "TOKEN=$(curl -s " + origin + "/api/cloud/auth/api-keys/exchange \\",
      "  -H 'Content-Type: application/json' \\",
      `  -d '{"token":"'"$BINO_API_KEY"'"}' | jq -r .token)`,
      "",
      "# 2. Render a report with that token",
      "curl -s '" + origin + "/v1/render?format=pdf' \\",
      '  -H "Authorization: Bearer $TOKEN" \\',
      "  --data-binary @report.yaml -o report.pdf",
    ].join("\n");
  }

  // ---- Render ----------------------------------------------------------------

  /** The two cards split one listApiKeys() load; missing kind = legacy general. */
  private get generalKeys(): ApiKey[] {
    return this.keys.filter((k) => k.kind !== "scoped");
  }

  private get scopedKeys(): ApiKey[] {
    return this.keys.filter((k) => k.kind === "scoped");
  }

  /** Shared cells: expiry as "Never" badge or date; revoke action button. */
  private expiresCell(k: ApiKey) {
    return k.expires
      ? this.fmtDate(k.expires)
      : html`<bn-badge variant="neutral">Never</bn-badge>`;
  }

  private revokeCell(k: ApiKey) {
    return html`<bn-button
      variant="danger"
      size="sm"
      @click=${() => (this.revokeTarget = k)}
      aria-label=${"Revoke API key " + k.name}
      >Revoke</bn-button
    >`;
  }

  private renderGeneralTable() {
    const columns: BnColumn[] = [
      { key: "name", label: "Name" },
      { key: "prefix", label: "Prefix", mono: true },
      { key: "created", label: "Created" },
      { key: "expires", label: "Expires" },
      { key: "lastUsed", label: "Last used" },
      { key: "actions", label: "", align: "right", width: "1px" },
    ];
    const rows: BnRow[] = this.generalKeys.map((k) => ({
      name: k.name,
      prefix: k.prefix,
      created: this.fmtDate(k.created),
      expires: this.expiresCell(k),
      lastUsed: k.lastUsed ? this.fmtDate(k.lastUsed) : html`<bn-badge variant="neutral">Never</bn-badge>`,
      actions: this.revokeCell(k),
    }));
    return html`<bn-table .columns=${columns} .rows=${rows} empty="No API keys yet."></bn-table>`;
  }

  private renderScopedTable() {
    const columns: BnColumn[] = [
      { key: "name", label: "Name" },
      { key: "prefix", label: "Prefix", mono: true },
      { key: "scope", label: "Scope" },
      { key: "access", label: "Access" },
      { key: "created", label: "Created" },
      { key: "expires", label: "Expires" },
      { key: "lastUsed", label: "Last used" },
      { key: "actions", label: "", align: "right", width: "1px" },
    ];
    const rows: BnRow[] = this.scopedKeys.map((k) => ({
      name: k.name,
      prefix: k.prefix,
      scope: this.scopeCell(k),
      access: this.accessCell(k),
      created: this.fmtDate(k.created),
      expires: this.expiresCell(k),
      lastUsed: k.lastUsed ? this.fmtDate(k.lastUsed) : html`<bn-badge variant="neutral">Never</bn-badge>`,
      actions: this.revokeCell(k),
    }));
    return html`<bn-table
      .columns=${columns}
      .rows=${rows}
      empty="No fine-grained tokens yet."
    ></bn-table>`;
  }

  /** "All teams", the team name, or "team / workspace" or "team / repository"
   * for the narrowest scope. */
  private scopeCell(k: ApiKey): string {
    const s = k.scope;
    if (!s?.team) return "All teams";
    const team = this.teamName(s.team);
    if (s.repository) return `${team} / ${this.repositoryNames[s.repository] ?? s.repository}`;
    return s.workspace ? `${team} / ${this.workspaceName(s.workspace)}` : team;
  }

  /** Feature count, with the full list in the hover title. */
  private accessCell(k: ApiKey) {
    const feats = k.scope?.features ?? [];
    const labels = feats.map((f) => FEATURES.find((x) => x.id === f)?.label ?? f);
    return html`<span title=${labels.join(", ")}
      >${feats.length} permission${feats.length === 1 ? "" : "s"}</span
    >`;
  }

  private renderList() {
    if (this.loading) {
      return html`<bn-card flush
        ><bn-spinner block label="Loading API keys…"></bn-spinner
      ></bn-card>`;
    }
    if (this.loadError) {
      return html`<bn-card flush>
        <div class="load-error">
          <span>${this.loadError}</span>
          <bn-button variant="secondary" size="sm" @click=${() => void this.load()}
            >Retry</bn-button
          >
        </div>
      </bn-card>`;
    }
    return html`${this.renderGeneralCard()} ${this.renderScopedCard()}`;
  }

  private renderGeneralCard() {
    if (this.generalKeys.length === 0) {
      return html`<bn-card flush>
        <bn-empty-state
          icon="🔑"
          heading="No API keys yet"
          message="Create a key to call the bino Cloud API from the CLI, CI, or your own scripts."
        >
          <bn-button slot="actions" variant="primary" @click=${() => this.openCreate()}
            >Create API key</bn-button
          >
        </bn-empty-state>
      </bn-card>`;
    }
    return html`<bn-card flush heading="API keys">
      <bn-button slot="actions" variant="primary" size="sm" @click=${() => this.openCreate()}
        >New key</bn-button
      >
      ${this.renderGeneralTable()}
    </bn-card>`;
  }

  private renderScopedCard() {
    return html`<bn-card flush heading="Fine-grained tokens">
      <bn-button slot="actions" variant="primary" size="sm" @click=${() => this.openScoped()}
        >New token</bn-button
      >
      ${this.renderScopedTable()}
    </bn-card>`;
  }

  private renderUsage() {
    const code = this.snippet();
    return html`<bn-card heading="Use a key">
      <p class="snippet-lead">
        You don't send an API key to the API directly. Exchange it for a short-lived token first.
        Git is the exception: it takes a fine-grained token for the repository as its password.
      </p>
      <div class="copy-row">
        <pre class="code">${code}</pre>
        <bn-button variant="secondary" size="sm" @click=${() => void this.copy(code, "Snippet copied")}
          >Copy</bn-button
        >
      </div>
    </bn-card>`;
  }

  private renderCreateModal() {
    const done = this.created !== null;
    return html`<bn-modal
      ?open=${this.createOpen}
      heading=${done ? "API key created" : "Create API key"}
      @close=${() => this.closeCreate()}
    >
      ${done ? this.renderReveal(this.created!) : this.renderCreateForm()}
      ${done
        ? html`<bn-button slot="footer" variant="primary" @click=${() => (this.createOpen = false)}
            >Done</bn-button
          >`
        : html`
            <bn-button slot="footer" variant="ghost" @click=${() => this.closeCreate()}
              >Cancel</bn-button
            >
            <bn-button
              slot="footer"
              variant="primary"
              ?loading=${this.creating}
              ?disabled=${this.newName.trim() === "" || this.expiresInSeconds() === null}
              @click=${() => void this.submitCreate()}
              >Create key</bn-button
            >
          `}
    </bn-modal>`;
  }

  private renderCreateForm() {
    return html`<bn-field
        label="Key name"
        required
        hint="Helps you recognize the key later, e.g. “ci-pipeline” or “laptop”."
      >
        <bn-input
          .value=${this.newName}
          placeholder="ci-pipeline"
          autocomplete="off"
          @input=${(e: CustomEvent) => (this.newName = (e.detail as { value: string }).value)}
        ></bn-input>
      </bn-field>
      ${this.renderLifetimeField()}`;
  }

  /** The shared lifetime picker: preset select, plus a date input on "custom"
   * and a warning hint on "unlimited". */
  private renderLifetimeField() {
    const custom = this.lifetime === "custom";
    const customError =
      custom && this.customDate !== "" && this.expiresInSeconds() === null
        ? "Pick a future date within the next 10 years."
        : "";
    return html`<bn-field
      label="Expiration"
      required
      hint=${this.lifetime === "unlimited" ? "This key works until you revoke it." : ""}
      error=${customError}
    >
      <div class="lifetime-row">
        <select
          aria-label="Expiration"
          .value=${this.lifetime}
          @change=${(e: Event) => (this.lifetime = (e.target as HTMLSelectElement).value as Lifetime)}
        >
          ${LIFETIMES.map(
            (o) => html`<option value=${o.value} ?selected=${o.value === this.lifetime}>
              ${o.label}
            </option>`,
          )}
        </select>
        ${custom
          ? html`<input
              type="date"
              aria-label="Expiration date"
              .value=${this.customDate}
              @input=${(e: Event) => (this.customDate = (e.target as HTMLInputElement).value)}
            />`
          : nothing}
      </div>
    </bn-field>`;
  }

  private renderScopedModal() {
    const done = this.created !== null;
    return html`<bn-modal
      ?open=${this.scopedOpen}
      width=${560}
      heading=${done ? "Fine-grained token created" : "Create fine-grained token"}
      @close=${() => this.closeScoped()}
    >
      ${done ? this.renderReveal(this.created!) : this.renderScopedForm()}
      ${done
        ? html`<bn-button slot="footer" variant="primary" @click=${() => (this.scopedOpen = false)}
            >Done</bn-button
          >`
        : html`
            <bn-button slot="footer" variant="ghost" @click=${() => this.closeScoped()}
              >Cancel</bn-button
            >
            <bn-button
              slot="footer"
              variant="primary"
              ?loading=${this.creating}
              ?disabled=${!this.scopedValid()}
              @click=${() => void this.submitScoped()}
              >Create token</bn-button
            >
          `}
    </bn-modal>`;
  }

  private renderScopedForm() {
    const enabled = FEATURES.filter((f) => this.featureEnabled(f.id)).map((f) => f.id);
    const allSelected = enabled.every((f) => this.scopedFeatures.includes(f));
    const teamWorkspaces = this.workspaces.filter((w) => w.teamId === this.scopedTeamId);
    return html`
      <bn-field
        label="Token name"
        required
        hint="Helps you recognize the token later, e.g. “ci-deploy”."
      >
        <bn-input
          .value=${this.scopedName}
          placeholder="ci-deploy"
          autocomplete="off"
          @input=${(e: CustomEvent) => (this.scopedName = (e.detail as { value: string }).value)}
        ></bn-input>
      </bn-field>
      ${this.renderLifetimeField()}
      <bn-field label="Team" hint="Limit the token to one team, or leave it usable on all your teams.">
        <select
          aria-label="Team"
          .value=${this.scopedTeamId}
          @change=${(e: Event) => void this.pickTeam((e.target as HTMLSelectElement).value)}
        >
          <option value="" ?selected=${this.scopedTeamId === ""}>All teams</option>
          ${this.teams.map(
            (t) => html`<option value=${t.id} ?selected=${t.id === this.scopedTeamId}>
              ${t.displayName || t.name}
            </option>`,
          )}
        </select>
      </bn-field>
      ${this.scopedTeamId !== ""
        ? html`<bn-field label="Workspace" hint="Narrow the token to a single workspace in the team.">
            <select
              aria-label="Workspace"
              .value=${this.scopedWorkspaceId}
              @change=${(e: Event) => {
                this.scopedWorkspaceId = (e.target as HTMLSelectElement).value;
                if (this.scopedWorkspaceId) this.pickRepository("");
              }}
            >
              <option value="" ?selected=${this.scopedWorkspaceId === ""}>Entire team</option>
              ${teamWorkspaces.map(
                (w) => html`<option value=${w.id} ?selected=${w.id === this.scopedWorkspaceId}>
                  ${w.name}
                </option>`,
              )}
            </select>
          </bn-field>`
        : nothing}
      ${this.scopedTeamId !== "" && this.teamRepositories.length > 0
        ? html`<bn-field
            label="Repository"
            hint="Narrow the token to one repository and its branch workspaces. Needed for git."
          >
            <select
              aria-label="Repository"
              .value=${this.scopedRepositoryId}
              @change=${(e: Event) => this.pickRepository((e.target as HTMLSelectElement).value)}
            >
              <option value="" ?selected=${this.scopedRepositoryId === ""}>No repository</option>
              ${this.teamRepositories.map(
                (r) => html`<option value=${r.id} ?selected=${r.id === this.scopedRepositoryId}>
                  ${r.name}
                </option>`,
              )}
            </select>
          </bn-field>`
        : nothing}
      <bn-field
        label="Permissions"
        required
        hint="The token can only call endpoints of the permissions you grant."
      >
        <div class="feature-head">
          <bn-checkbox
            label="Select all"
            .checked=${allSelected}
            @change=${(e: CustomEvent<{ checked: boolean }>) =>
              (this.scopedFeatures = e.detail.checked ? enabled : [])}
          ></bn-checkbox>
        </div>
        <div class="feature-grid">
          ${FEATURES.map(
            (f) => html`<bn-checkbox
              label=${f.label}
              hint=${f.hint}
              ?disabled=${!this.featureEnabled(f.id)}
              .checked=${this.scopedFeatures.includes(f.id)}
              @change=${(e: CustomEvent<{ checked: boolean }>) =>
                this.toggleFeature(f.id, e.detail.checked)}
            ></bn-checkbox>`,
          )}
        </div>
      </bn-field>
      ${this.scopedFeatures.includes("git")
        ? html`<bn-field label="Git access" hint="Clone only, or clone and push.">
            <select
              aria-label="Git access"
              .value=${this.scopedGitAccess}
              @change=${(e: Event) =>
                (this.scopedGitAccess = (e.target as HTMLSelectElement).value as GitAccess)}
            >
              <option value="read" ?selected=${this.scopedGitAccess === "read"}>Read (clone)</option>
              <option value="rw" ?selected=${this.scopedGitAccess === "rw"}>
                Read and write (clone and push)
              </option>
            </select>
          </bn-field>`
        : nothing}
    `;
  }

  /** The extra "are you sure" step when the lifetime picker says Unlimited;
   * mirrors the revoke confirm below. Rendered after the create modals so it
   * stacks above whichever one is open. */
  private renderUnlimitedModal() {
    return html`<bn-modal
      ?open=${this.unlimitedConfirm !== null}
      heading="Create a key that never expires?"
      @close=${() => (this.unlimitedConfirm = null)}
    >
      <p>
        A key without an expiry date works until you revoke it, even if it leaks. Set an expiry
        date unless the key must never expire.
      </p>
      <bn-button slot="footer" variant="ghost" @click=${() => (this.unlimitedConfirm = null)}
        >Cancel</bn-button
      >
      <bn-button slot="footer" variant="danger" @click=${() => this.confirmUnlimited()}
        >Create without expiry</bn-button
      >
    </bn-modal>`;
  }

  /** The step-up gate: minting a long-lived credential demands a fresh proof
   * of presence — a passkey assertion (when any are registered) or an emailed
   * one-time code. Rendered after every other modal so it stacks above
   * whichever create flow it paused. */
  private renderVerifyModal() {
    return html`<bn-modal
      ?open=${this.verifyFor !== null}
      heading="Verify it's you"
      @close=${() => this.closeVerify()}
    >
      <p class="modal-copy">
        ${this.verifyOtpSent
          ? "We sent a one-time code to your email."
          : "An API key gives long-lived access to your account. Confirm it's you before you create one."}
      </p>
      ${this.hasPasskeys
        ? html`<div class="verify-passkey">
            <bn-button
              variant="secondary"
              block
              ?disabled=${this.verifyBusy}
              @click=${() => void this.verifyPasskey()}
              >Use a passkey</bn-button
            >
          </div>`
        : nothing}
      ${this.verifyOtpSent
        ? html`<bn-field label="One-time code" required>
            <bn-input
              mono
              inputmode="numeric"
              autocomplete="one-time-code"
              placeholder="123456"
              ?disabled=${this.verifyBusy}
              .value=${this.verifyCode}
              @input=${(e: CustomEvent) => (this.verifyCode = (e.detail as { value: string }).value)}
              @keydown=${(e: KeyboardEvent) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void this.confirmVerifyCode();
                }
              }}
            ></bn-input>
          </bn-field>`
        : nothing}
      <bn-button slot="footer" variant="ghost" @click=${() => this.closeVerify()}>Cancel</bn-button>
      ${this.verifyOtpSent
        ? html`<bn-button
            slot="footer"
            variant="primary"
            ?loading=${this.verifyBusy}
            ?disabled=${this.verifyCode.trim() === ""}
            @click=${() => void this.confirmVerifyCode()}
            >Verify</bn-button
          >`
        : html`<bn-button
            slot="footer"
            variant="primary"
            ?loading=${this.verifyBusy}
            @click=${() => void this.sendVerifyCode()}
            >Email me a code</bn-button
          >`}
    </bn-modal>`;
  }

  private renderReveal(key: ApiKeyCreated) {
    return html`
      <p class="reveal-label">Your new API key</p>
      <div class="copy-row">
        <pre class="code token">${key.token}</pre>
        <bn-button
          variant="secondary"
          size="sm"
          @click=${() => void this.copy(key.token, "API key copied")}
          >Copy</bn-button
        >
      </div>
      <div class="warn" role="alert">
        <span class="warn-icon" aria-hidden="true">!</span>
        <span
          >Copy this key now. It is not shown again. If you lose it, revoke it and create a new
          one.</span
        >
      </div>
    `;
  }

  private renderDocs() {
    // RapiDoc derives hover/active shades from its color attributes, so they
    // are the literal values behind the --bino-* tokens, not var() references
    // (it cannot resolve those). update-route stays off so RapiDoc's own hash
    // routing doesn't fight the app router.
    return html`<bn-card flush heading="API reference">
      <a class="spec-link" slot="actions" href="/v1/openapi.yaml" download="openapi.yaml"
        >Download openapi.yaml</a
      >
      <rapi-doc
        spec-url="/v1/openapi.json"
        server-url=${window.location.origin}
        render-style="read"
        show-header="false"
        update-route="false"
        allow-server-selection="false"
        allow-spec-url-load="false"
        allow-spec-file-load="false"
        allow-try="true"
        allow-authentication="true"
        load-fonts="false"
        theme="light"
        bg-color="#ffffff"
        text-color="#11161a"
        primary-color="#0b727e"
        nav-bg-color="#f6f8f9"
        regular-font="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
        mono-font="'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace"
      ></rapi-doc>
    </bn-card>`;
  }

  private renderRevokeModal() {
    const target = this.revokeTarget;
    return html`<bn-modal
      ?open=${target !== null}
      heading="Revoke API key?"
      @close=${() => {
        if (!this.revoking) this.revokeTarget = null;
      }}
    >
      ${target
        ? html`<p>
            The key <span class="confirm-name">${target.name}</span> will stop working
            immediately. Anything using it will lose access. This cannot be undone.
          </p>`
        : nothing}
      <bn-button
        slot="footer"
        variant="ghost"
        @click=${() => {
          if (!this.revoking) this.revokeTarget = null;
        }}
        >Cancel</bn-button
      >
      <bn-button
        slot="footer"
        variant="danger"
        ?loading=${this.revoking}
        @click=${() => void this.confirmRevoke()}
        >Revoke key</bn-button
      >
    </bn-modal>`;
  }

  override render() {
    return html`
      <div class="head">
        <div class="head-text">
          <h1>API</h1>
        </div>
        ${!this.loading && !this.loadError && this.keys.length > 0
          ? html`<bn-button variant="primary" @click=${() => this.openCreate()}
              >Create API key</bn-button
            >`
          : nothing}
      </div>

      <div class="stack">${this.renderList()} ${this.renderUsage()} ${this.renderDocs()}</div>

      ${this.renderCreateModal()} ${this.renderScopedModal()} ${this.renderRevokeModal()}
      ${this.renderUnlimitedModal()} ${this.renderVerifyModal()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-api-keys": PageApiKeys;
  }
}
